import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../ui/alert-dialog';
import { Plus, Pencil, Trash2, User, Search, Target, CheckCircle2, XCircle, BarChart3 } from 'lucide-react';
import { apiListOperators, apiCreateOperator, apiUpdateOperator, apiDeleteOperator } from '../../api';
import { toast } from 'sonner';

const empty = { first_name: '', last_name: '', alias: '', target_eur_per_hour: 15, notes: '', active: true };

const OperatorForm = ({ initial, onSubmit, onCancel }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState(initial ? { ...initial } : empty);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.alias.trim()) {
      toast.error('Nome, cognome e alias sono obbligatori');
      return;
    }
    onSubmit({
      ...form,
      target_eur_per_hour: parseFloat(form.target_eur_per_hour) || 0
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Nome <span className="text-red-500">*</span></Label>
          <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required />
        </div>
        <div className="space-y-2">
          <Label>Cognome <span className="text-red-500">*</span></Label>
          <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Alias <span className="text-red-500">*</span></Label>
        <Input placeholder="es. MR01" value={form.alias} onChange={(e) => setForm({ ...form, alias: e.target.value })} className="font-mono uppercase" required />
        <p className="text-[11px] text-slate-500">Codice univoco operatore (usato per anonimizzazione)</p>
      </div>
      <div className="space-y-2">
        <Label>Target produttività (€/ora)</Label>
        <Input type="number" step="0.01" min="0" value={form.target_eur_per_hour} onChange={(e) => setForm({ ...form, target_eur_per_hour: e.target.value })} />
        <p className="text-[11px] text-slate-500">Sopra questo valore = verde, sotto = rosso. Lasciare 0 per disabilitare.</p>
      </div>
      <div className="space-y-2">
        <Label>Note (opzionale)</Label>
        <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="resize-none" />
      </div>
      <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
        <div>
          <div className="text-sm font-medium">Operatore attivo</div>
          <div className="text-xs text-slate-500">Solo gli operatori attivi appaiono nei form di inserimento</div>
        </div>
        <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Annulla</Button>
        <Button type="submit" className="bg-blue-700 hover:bg-blue-800">{isEdit ? 'Salva' : 'Crea operatore'}</Button>
      </DialogFooter>
    </form>
  );
};

const OperatorsManager = ({ onView }) => {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const reload = () => apiListOperators().then(setList).catch(() => setList([]));
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = list.filter(o => {
    if (!search) return true;
    const q = search.toLowerCase();
    return o.first_name.toLowerCase().includes(q) || o.last_name.toLowerCase().includes(q) || o.alias.toLowerCase().includes(q);
  });

  const handleSubmit = async (data) => {
    try {
      if (editing) {
        await apiUpdateOperator(editing.id, data);
        toast.success('Operatore aggiornato');
      } else {
        await apiCreateOperator(data);
        toast.success('Operatore creato');
      }
      setDialogOpen(false);
      setEditing(null);
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await apiDeleteOperator(toDelete.id);
      toast.success('Operatore eliminato');
      setToDelete(null);
      reload();
    } catch (err) { toast.error('Errore eliminazione'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Cerca operatore o alias..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 pl-8 w-[280px] text-sm" />
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-blue-700 hover:bg-blue-800 gap-2">
              <Plus className="w-4 h-4" /> Nuovo operatore
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Modifica operatore' : 'Nuovo operatore'}</DialogTitle>
              <DialogDescription>Anagrafica e target di produttività.</DialogDescription>
            </DialogHeader>
            <OperatorForm initial={editing} onSubmit={handleSubmit} onCancel={() => { setDialogOpen(false); setEditing(null); }} />
          </DialogContent>
        </Dialog>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300">
          <User className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="text-sm font-medium text-slate-700">Nessun operatore</div>
          <p className="text-xs text-slate-500 mt-1">Clicca "Nuovo operatore" per iniziare</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(o => (
            <div key={o.id} className={`p-4 rounded-xl border bg-white ${!o.active ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-700 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {o.alias.slice(0, 4).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-slate-900 truncate">{o.first_name} {o.last_name}</div>
                    <div className="text-xs text-slate-500 font-mono">{o.alias}</div>
                  </div>
                </div>
                {o.active ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                ) : (
                  <XCircle className="w-4 h-4 text-slate-400" />
                )}
              </div>
              {o.target_eur_per_hour > 0 && (
                <div className="text-xs text-slate-700 bg-slate-50 rounded-md p-2 mb-2 flex items-center gap-1.5">
                  <Target className="w-3 h-3 text-blue-700" />
                  Target: <strong>€{o.target_eur_per_hour.toFixed(2)}/ora</strong>
                </div>
              )}
              {o.notes && <div className="text-xs text-slate-600 mb-2 italic">{o.notes}</div>}
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" className="flex-1 h-8 gap-1.5 text-xs" onClick={() => onView?.(o)}>
                  <BarChart3 className="w-3 h-3" /> Storico
                </Button>
                <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={() => { setEditing(o); setDialogOpen(true); }}>
                  <Pencil className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50" onClick={() => setToDelete(o)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {toDelete?.alias}?</AlertDialogTitle>
            <AlertDialogDescription>
              Saranno eliminate anche tutte le sessioni di produzione e i prezzi personalizzati per questo operatore. <strong>Azione irreversibile.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OperatorsManager;
